import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDocs } from "../hooks/useDoc";
import { useIntegration } from "../hooks/useIntegration";
import { useSelector } from "react-redux";
import { useUserMsp } from "../hooks/useUserMsp";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import RichTextEditor from "./RichTextEditor";
import { plainTextToHtml, hasHtmlTags } from "../utils/htmlUtils";
import LayeredAutocompleteSelect from "./LayeredAutocompleteSelect";

const DocFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // id is undefined for add, present for edit
  const isEdit = Boolean(id);

  const {
    docs,
    selectedDoc,
    createDoc,
    updateDoc,
    deleteDoc,
    fetchDocs,
    fetchDocById,
    isLoading: docsLoading,
    selectedCustomerId,
    setCustomerSelection,
  } = useDocs();
  const {
    customers,
    getCustomers,
    isLoading: customersLoading,
  } = useIntegration();
  const user = useSelector((state: any) => state.auth.user);
  const { userMspInfo } = useUserMsp();

  // Filter customers by MSP ID and include 'root' customer (displayed as "All")
  const filteredCustomers = useMemo(() => {
    let arr = (customers || []).filter((c: any) => {
      return (
        c.name?.toLowerCase() === "root" ||
        String(c.msp_id) === String(userMspInfo?.msp_id)
      );
    });
    // Ensure 'All' (root) is first
    arr = [
      ...arr.filter((c: any) => c.name?.toLowerCase() === "root"),
      ...arr.filter((c: any) => c.name?.toLowerCase() !== "root" && !isMspOrgCustomer(c)),
    ];
    return arr;
  }, [customers, userMspInfo?.msp_id]);

  // Get preselected customer from location state (if any)
  // If "all" is passed, it means "All Customer" was selected, so don't preselect any customer
  let preselectedCustomer = location.state?.selectedCustomer;
  if (preselectedCustomer === "all") {
    preselectedCustomer = ""; // No customer preselected when "All Customer" is selected
  } else if (preselectedCustomer === undefined || preselectedCustomer === null || preselectedCustomer === "") {
    // If no preselected customer from location state, use selectedCustomerId or first customer
    // But check if selectedCustomerId is "All Customer" (root), if so, don't preselect
    const rootCustomer = filteredCustomers.find((c: any) => c.name?.toLowerCase() === "root");
    const isAllCustomerSelected = selectedCustomerId === rootCustomer?.id?.toString() || selectedCustomerId === "all";
    if (isAllCustomerSelected) {
      preselectedCustomer = "";
    } else {
      preselectedCustomer = selectedCustomerId || (filteredCustomers.length > 0 ? filteredCustomers[0].id : "");
    }
  }

  // Find doc if editing - prefer selectedDoc over docs array
  const initialDoc = isEdit
    ? selectedDoc || docs.find((d: any) => d.id === Number(id))
    : null;

  // Form state
  const [customerId, setCustomerId] = useState(
    isEdit 
      ? (initialDoc?.customer_id !== undefined && initialDoc?.customer_id !== null ? initialDoc.customer_id : "")
      : (preselectedCustomer !== undefined && preselectedCustomer !== null ? preselectedCustomer : "")
  );
  const [content, setContent] = useState(initialDoc?.content || "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch customers when component mounts
  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  // Fetch specific doc when editing
  useEffect(() => {
    if (isEdit && id) {
      fetchDocById(id);
    }
  }, [isEdit, id, fetchDocById]);

  // Update form fields when initialDoc changes (after docs are loaded)
  useEffect(() => {
    if (isEdit && initialDoc) {
      setCustomerId(initialDoc.customer_id !== undefined && initialDoc.customer_id !== null ? initialDoc.customer_id : "");
      // Convert plain text to HTML for backward compatibility with existing docs
      const contentValue = initialDoc.content || "";
      const contentHtml = hasHtmlTags(contentValue) ? contentValue : plainTextToHtml(contentValue);
      setContent(contentHtml);
    }
  }, [isEdit, initialDoc]);

  // Update customerId when filteredCustomers change (for new doc)
  // Don't auto-select if preselectedCustomer is empty (meaning "All Customer" was selected)
  useEffect(() => {
    if (!isEdit && filteredCustomers.length > 0 && (customerId === "" || customerId === null || customerId === undefined)) {
      // Only auto-select if preselectedCustomer is not empty
      if (preselectedCustomer !== "" && preselectedCustomer !== null && preselectedCustomer !== undefined) {
        setCustomerId(preselectedCustomer);
      }
    }
  }, [isEdit, filteredCustomers, customerId, preselectedCustomer]);

  // Update Redux state when customer selection changes
  useEffect(() => {
    if (customerId) {
      setCustomerSelection(customerId);
    }
  }, [customerId, setCustomerSelection]);

  // Check if we're still loading data
  const isLoading = docsLoading || customersLoading;

  // Check if we have the doc data when editing
  const hasDocData = !isEdit || (isEdit && initialDoc);

  const handleSave = () => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds()
    )}`;
    const data = {
      customer_id: customerId,
      content,
      msp_id: userMspInfo?.msp_id,
    };
    if (isEdit) {
      updateDoc({ ...initialDoc, ...data, last_updated: dateStr });
      navigate(`/docs/${id}`);
    } else {
      createDoc({
        ...data,
        created_by: user?.name || user?.email || "Unknown",
        date_added: dateStr,
        last_updated: dateStr,
      });
      navigate("/docs", { state: { selectedCustomer: selectedCustomerId } });
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteDoc(id);
      navigate("/docs", { state: { selectedCustomer: selectedCustomerId } });
    }
  };

  // Show loading state if we're still fetching data
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate("/docs", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1 }}>
            {isEdit ? "Edit a Doc" : "Add a Doc"}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Paper>
    );
  }

  // Show error state if editing but no doc data found
  if (isEdit && !hasDocData) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate("/docs", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1 }}>
            Edit a Doc
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error">
            Doc not found or still loading...
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/docs", { state: { selectedCustomer: selectedCustomerId } })}
            sx={{ mt: 2 }}
          >
            Back to Docs
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        mx: 1,
        my: 1,
        p: { xs: 2, sm: 3, md: 4 },
        background: "#FFF",
        borderRadius: 1,
        fontFamily: "var(--theo-font-sans)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => navigate("/docs", { state: { selectedCustomer: selectedCustomerId } })}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ ml: 1 }}>
          {isEdit ? "Edit a Doc" : "Add a Doc"}
        </Typography>
      </Box>
      {/* Created By Display (only for edit mode) */}
      {isEdit && initialDoc?.created_by && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, px: 1 }}>
          <Typography sx={{ fontWeight: 500, fontFamily: "var(--theo-font-sans)", color: "#838383", fontSize: 14 }}>
            Created by:
          </Typography>
          <Typography sx={{ ml: 1, fontFamily: "var(--theo-font-sans)", fontSize: 14 }}>
            {initialDoc.created_by}
          </Typography>
        </Box>
      )}
      {/* Customer Selector */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
          Customer
        </Typography>
        <LayeredAutocompleteSelect
          label="Customer"
          value={String(customerId ?? "")}
          onChange={setCustomerId}
          disabled={isLoading}
          options={filteredCustomers.map((c: any) => ({
            value: String(c.id),
            label: c.name?.toLowerCase() === "root" ? "All Customer" : c.name,
          }))}
          noOptionsText="No customers available"
          sx={{ flex: 1, minWidth: 300 }}
          textFieldSx={{ "& .MuiInputBase-root": { fontFamily: "var(--theo-font-sans)" } }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
        <Typography
          sx={{ width: 140, fontWeight: 500, fontFamily: "var(--theo-font-sans)", pt: 1 }}
        >
          Content
        </Typography>
        <FormControl sx={{ flex: 1, minWidth: 300 }}>
          <RichTextEditor
            value={content}
            onChange={setContent}
            disabled={isLoading}
            placeholder="Enter document content..."
            minHeight="400px"
          />
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 2, justifyContent: "space-between" }}>
        <Box>
          {isEdit && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isLoading}
            >
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={customerId === "" || customerId === null || customerId === undefined || !content || isLoading}
          >
            {isLoading ? "Loading..." : "Save"}
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate(isEdit ? `/docs/${id}` : "/docs")}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ fontFamily: "var(--theo-font-sans)" }}>
          Delete Doc
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ fontFamily: "var(--theo-font-sans)" }}>
            Are you sure you want to delete this doc? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: "var(--theo-font-sans)" }}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              handleDelete();
            }} 
            color="error" 
            variant="contained"
            sx={{ fontFamily: "var(--theo-font-sans)" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DocFormPage;
